import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { DollarSign, Users, Clock, TrendingUp, Activity, BarChart3, Mail, RefreshCw } from 'lucide-react';
import { useAdmin } from './AdminContext';

export default function CostsTab({ refreshTrigger }) {
  const { adminFetch, API_URL } = useAdmin();
  const [costs, setCosts] = useState(null);

  const loadCosts = useCallback(async () => {
    try {
      const response = await adminFetch(`${API_URL}/api/v2/admin/costs`);
      if (response.ok) {
        const data = await response.json();
        setCosts(data);
      }
    } catch (error) {
      console.error('Error loading costs:', error);
    }
  }, [adminFetch, API_URL]);

  useEffect(() => { loadCosts(); }, [loadCosts, refreshTrigger]);

  return (
    <div className="space-y-6" data-testid="costs-tab">
      {costs ? (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="bg-gradient-to-br from-red-500/20 to-red-600/20 border-red-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-red-300 text-sm">Monthly Cost</p>
                    <p className="text-3xl font-bold text-white">${costs.summary.total_cost_month.toFixed(2)}</p>
                  </div>
                  <DollarSign className="h-10 w-10 text-red-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-500/20 to-orange-600/20 border-orange-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-300 text-sm">Cost per User</p>
                    <p className="text-3xl font-bold text-white">${costs.summary.cost_per_user.toFixed(4)}</p>
                  </div>
                  <Users className="h-10 w-10 text-orange-400" />
                </div>
                <p className="text-xs text-orange-300 mt-2">{costs.summary.total_users} total users</p>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-yellow-500/20 to-yellow-600/20 border-yellow-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-yellow-300 text-sm">Daily Cost</p>
                    <p className="text-3xl font-bold text-white">${costs.summary.cost_per_day.toFixed(4)}</p>
                  </div>
                  <Clock className="h-10 w-10 text-yellow-400" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-500/20 to-green-600/20 border-green-500/30">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-300 text-sm">Monthly Profit</p>
                    <p className="text-3xl font-bold text-white">${costs.summary.profit_month.toFixed(2)}</p>
                  </div>
                  <TrendingUp className="h-10 w-10 text-green-400" />
                </div>
                <p className="text-xs text-green-300 mt-2">Revenue: ${costs.summary.revenue_month.toFixed(2)}</p>
              </CardContent>
            </Card>
          </div>

          {/* Cost Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Activity className="h-5 w-5 text-purple-400" />
                  AI Costs (Claude)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">AI Plans Generated</span>
                  <span className="text-white font-medium">{costs.breakdown.ai?.plans_generated || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Menu Scans</span>
                  <span className="text-white font-medium">{costs.breakdown.ai?.menu_scans || 0}</span>
                </div>
                <div className="border-t border-slate-700 pt-3 flex justify-between items-center">
                  <span className="text-slate-300 font-medium">Total AI Cost</span>
                  <span className="text-purple-400 font-bold">${costs.breakdown.ai?.cost?.toFixed(4) || '0.00'}</span>
                </div>
                <p className="text-xs text-slate-500">{costs.breakdown.ai?.rate_info}</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <BarChart3 className="h-5 w-5 text-blue-400" />
                  AWS S3 Storage
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Photos Stored</span>
                  <span className="text-white font-medium">{costs.breakdown.s3?.photos_stored || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Backups Stored</span>
                  <span className="text-white font-medium">{costs.breakdown.s3?.backups_stored || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Estimated Storage</span>
                  <span className="text-white font-medium">{costs.breakdown.s3?.estimated_gb?.toFixed(3) || '0'} GB</span>
                </div>
                <div className="border-t border-slate-700 pt-3 flex justify-between items-center">
                  <span className="text-slate-300 font-medium">Total S3 Cost</span>
                  <span className="text-blue-400 font-bold">${costs.breakdown.s3?.cost?.toFixed(4) || '0.00'}</span>
                </div>
                <p className="text-xs text-slate-500">{costs.breakdown.s3?.rate_info}</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="h-5 w-5 text-cyan-400" />
                  Email (Resend)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Emails Sent This Month</span>
                  <span className="text-white font-medium">{costs.breakdown.email?.emails_sent || 0}</span>
                </div>
                <div className="border-t border-slate-700 pt-3 flex justify-between items-center">
                  <span className="text-slate-300 font-medium">Total Email Cost</span>
                  <span className="text-cyan-400 font-bold">${costs.breakdown.email?.cost?.toFixed(4) || '0.00'}</span>
                </div>
                <p className="text-xs text-slate-500">{costs.breakdown.email?.rate_info}</p>
              </CardContent>
            </Card>

            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="h-5 w-5 text-green-400" />
                  Stripe Fees
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Transactions This Month</span>
                  <span className="text-white font-medium">{costs.breakdown.stripe?.transactions || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-slate-400">Revenue Processed</span>
                  <span className="text-white font-medium">${costs.breakdown.stripe?.revenue?.toFixed(2) || '0.00'}</span>
                </div>
                <div className="border-t border-slate-700 pt-3 flex justify-between items-center">
                  <span className="text-slate-300 font-medium">Total Stripe Fees</span>
                  <span className="text-green-400 font-bold">${costs.breakdown.stripe?.cost?.toFixed(4) || '0.00'}</span>
                </div>
                <p className="text-xs text-slate-500">{costs.breakdown.stripe?.rate_info}</p>
              </CardContent>
            </Card>
          </div>

          {/* Rate Card */}
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Cost Rates Reference</CardTitle>
              <CardDescription className="text-slate-400">Estimated rates used for calculations</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-slate-400">Claude Input</p>
                  <p className="text-white font-medium">$0.003/1K tokens</p>
                </div>
                <div>
                  <p className="text-slate-400">Claude Output</p>
                  <p className="text-white font-medium">$0.015/1K tokens</p>
                </div>
                <div>
                  <p className="text-slate-400">S3 Storage</p>
                  <p className="text-white font-medium">$0.023/GB/month</p>
                </div>
                <div>
                  <p className="text-slate-400">Stripe</p>
                  <p className="text-white font-medium">2.9% + $0.30</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <div className="text-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-4" />
          <p className="text-slate-400">Loading cost data...</p>
        </div>
      )}
    </div>
  );
}
